/** tm/legal/useLegalAlerts.ts — 새 소식(알림): 보이는 동안 60초 · 숨김 5분마다 + 창 포커스·메뉴 이동 시 갱신.
 *  업무관리 화면 전체가 같은 값을 쓴다 (사이드바 배지·팝업·벨·대시보드) — 모듈 단위 저장소 + useSyncExternalStore.
 *  브라우저 탭 제목 앞에 "(n) " 을 붙이고, 수가 늘면 pulse 를 올려 배지가 한 번 깜빡이게 한다. */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { getAlerts } from './legalApi';
import type { LegalAlerts } from './legalTypes';

const VISIBLE_MS = 60 * 1000;
const HIDDEN_MS = 5 * 60 * 1000;
const FOCUS_GAP_MS = 10 * 1000;
const EMPTY: LegalAlerts = { total: 0, groups: [], newsToday: 0 };

// ── 공유 저장소 ──
let snap: LegalAlerts = EMPTY;
let loaded = false;
const listeners = new Set<() => void>();
function publish(next: LegalAlerts) { snap = next; loaded = true; listeners.forEach(l => l()); }
function subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }
const getSnap = () => snap;

/** 폴링 없이 현재 값만 읽기 (대시보드 카드 등) — 첫 값을 받기 전이면 loaded=false */
export function useAlertsSnapshot(): { data: LegalAlerts; loaded: boolean } {
  const data = useSyncExternalStore(subscribe, getSnap, getSnap);
  return { data, loaded: loaded || data !== EMPTY };
}

function normalize(d: LegalAlerts | null | undefined): LegalAlerts {
  if (!d) return EMPTY;
  return {
    total: Number(d.total) || 0, groups: Array.isArray(d.groups) ? d.groups : [], newsToday: Number(d.newsToday) || 0,
    latest_first_seen_at: d.latest_first_seen_at ?? null, top: Array.isArray(d.top) ? d.top : undefined,
  };
}

export interface LegalAlertsState {
  total: number;
  data: LegalAlerts;
  refresh: () => Promise<void>;
  /** 낙관적 반영 (읽음 처리 직후 등) */
  set: (next: LegalAlerts) => void;
  /** 새 소식 수가 늘 때마다 1씩 증가 (배지 깜빡임 key) */
  pulse: number;
}

/** 폴링 주체 — 업무관리 셸(TaskManagement)에서 한 번만 쓴다 */
export function useLegalAlerts(section: string): LegalAlertsState {
  const data = useSyncExternalStore(subscribe, getSnap, getSnap);
  const [pulse, setPulse] = useState(0);
  const alive = useRef(true);
  const lastAt = useRef(0);
  const refresh = useCallback(async () => {
    lastAt.current = Date.now();
    try {
      const d = normalize(await getAlerts());
      if (!alive.current) return;
      if (d.total > snap.total) setPulse(p => p + 1);
      publish(d);
    } catch { /* 알림 수는 부가 정보 — 실패해도 조용히 둔다 */ }
  }, []);

  // 보이는 동안 60초 · 숨김 5분 — 탭이 다시 보이면 바로 한 번
  useEffect(() => {
    alive.current = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => { await refresh(); schedule(); }, document.hidden ? HIDDEN_MS : VISIBLE_MS);
    };
    const onVis = () => { if (!document.hidden && Date.now() - lastAt.current > FOCUS_GAP_MS) refresh(); schedule(); };
    const onFocus = () => { if (Date.now() - lastAt.current > FOCUS_GAP_MS) refresh(); };
    schedule();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      alive.current = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);
  useEffect(() => { refresh(); }, [section, refresh]);

  // 탭 제목 "(n) 원래 제목"
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\+?\) /, '');
    document.title = data.total > 0 ? `(${data.total > 99 ? '99+' : data.total}) ${base}` : base;
  }, [data.total]);
  useEffect(() => () => { document.title = document.title.replace(/^\(\d+\+?\) /, ''); }, []);

  const set = useCallback((next: LegalAlerts) => publish(normalize(next)), []);
  return { total: data.total, data, refresh, set, pulse };
}

/** 홈 화면 타일용 — 한 번만 가볍게 받아 수만 돌려준다 */
export function useLegalAlertCount(enabled: boolean): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let on = true;
    getAlerts().then(d => { if (on) setN(Number(d?.total) || 0); }, () => undefined);
    return () => { on = false; };
  }, [enabled]);
  return n;
}
